import {CUSTOM_ELEMENTS_SCHEMA, NgModule} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';

import {LoadingBarComponent} from './loading-bar/loading-bar.component';
import {LoadingButtonComponent} from './loading-button/loading-button.component';
import { MethodologyAnimatedComponent } from './methodology-animated/methodology-animated.component';
import {BreadcrumbComponent} from 'app/shared/breadcrumb/breadcrumb.component';
import {RelatedSolutionsComponent} from 'app/shared/related-solutions/related-solutions.component';
import {PaginationComponent} from 'app/shared/pagination/pagination.component';
import {ListCitiesComponent} from 'app/shared/list-cities/list-cities.component';
import {HighlightSolutionComponent} from 'app/shared/highlight-solution/highlight-solution.component';
import {PaginateComponent} from 'app/shared/paginate/paginate.component';
import {NotFoundComponent} from 'app/shared/404/404.component';
import {TranslateModule, TranslateService} from '@ngx-translate/core';

import {InternationalizationService,} from 'app/shared/services/internationalization.service';
import {LanguageService, TranslatedLanguage} from './services/language.service';
import {FooterKnowMoreComponent} from './footer-know-more/footer-know-more.component';
import {
	IndicatorsDataYearEditorComponent
} from './indicators/indicators-data-year-editor/indicators-data-year-editor.component';
import {
	IndicatorsSingleDataModalComponent
} from './indicators/modal/indicators-single-data-modal/indicators-single-data-modal.component';
import {
	IndicatorsMultipleDataModalComponent
} from './indicators/modal/indicators-multiple-data-modal/indicators-multiple-data-modal.component';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatChipsModule} from '@angular/material/chips';
import {MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule} from '@angular/material/form-field';
import {MatDialogModule} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatListModule} from '@angular/material/list';
import {MatMenuModule} from '@angular/material/menu';
import {MatPaginatorModule} from '@angular/material/paginator';
import {MatRadioModule} from '@angular/material/radio';
import {MatSelectModule} from '@angular/material/select';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {MatTableModule} from '@angular/material/table';
import {MatTabsModule} from '@angular/material/tabs';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {UploadImageModalComponent} from './upload-image-modal/upload-image-modal.component';
import {FileUploadModule} from 'ng2-file-upload';
import {SmallPieChartComponent} from './small-pie-chart/small-pie-chart.component';
import { NgChartsModule } from 'ng2-charts';
import { NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import {
	IndicatorsBenchmarkTableComponent
} from './indicators/indicators-benchmark-table/indicators-benchmark-table.component';
import {PastYearDirective} from './validation/past-year.directive';
import { TextMaskModule } from 'app/shared/compat/text-mask.module';

import {InputMaskDirective} from './validation/input-mask.directive';
import {CityProfileService} from 'app/restricted/city-profile/service/city-profile.service';
import {ValidatePercentageDirective} from './validation/validate-percentage.directive';
import {OrdinalNumberPipe} from './util/ordinal-number.pipe';
import {GenericChipManagerComponent} from './generic-chip-manager/generic-chip-manager.component';
import {
	RestrictedInternationalizationComponent
} from './restricted-internationalization/restricted-internationalization.component';
import {RouterModule} from '@angular/router';
import {FilterReferencePipe} from './indicators/modal/indicators-single-data-modal/filter-reference.pipe';
import {FilterArrayPipe} from './indicators/modal/indicators-single-data-modal/filter-array.pipe';
import {SolutionUtilsService} from './solutions/solution-utils.service';
import {SafePipe} from './util/safe.pipe';
import {OdsIndicatorComponent} from './indicators/ods-indicator/ods-indicator.component';
import {AccordionLandingComponent} from './accordion-landing/accordion-landing.component';
import {StayConnectedComponent} from './stay-connected/stay-connected.component';
import {RdstationService} from './services/rdstation.service';
import {ButtonComponent} from './button/button.component';
import {CertificationIsoAbntComponent} from './certification/certification-iso-abnt/certification-iso-abnt.component';
import {MatButtonModule} from '@angular/material/button';
import {
	CertificationIsoAbntAreaComponent
} from './certification/certification-iso-abnt-area/certification-iso-abnt-area.component';
import {
	CertificationIsoAbntAreaFormComponent
} from './certification/certification-iso-abnt-area-form/certification-iso-abnt-area-form.component';
import {DragDropModule} from '@angular/cdk/drag-drop';
import {PdfViewerModule} from 'ng2-pdf-viewer';
import {DialogShowEvidenceComponent} from './certification/dialog-show-evidence/dialog-show-evidence.component';
import {DialogAddLinkComponent} from './certification/dialog-add-link/dialog-add-link.component';
import {PlansComponent} from './plans/plans.component';
import {OurNumbersComponent} from './our-numbers/our-numbers.component';
import { CarouselModule } from 'primeng/carousel';
import { CdkAccordionModule } from '@angular/cdk/accordion';
import { DialogAttachEvidenceComponent } from './certification/dialog-attach-evidence/dialog-attach-evidence.component';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { CreditCardIconComponent } from './credit-card-brand/credit-card-icon.component';
import { BtnLoadingComponent } from './btn-loading/btn-loading.component';
import { LoaderComponent } from './loader/loader.component';
import { CotationContactComponent } from './cotation-contact/cotation-contact.component';

@NgModule({
	imports: [
		CommonModule,
		RouterModule,
		FormsModule,
		ReactiveFormsModule,
		MatDialogModule,
		MatFormFieldModule,
		MatButtonModule,
		MatInputModule,
		MatIconModule,
		MatTableModule,
		MatSelectModule,
		MatSnackBarModule,
		MatPaginatorModule,
		MatTabsModule,
		MatListModule,
		MatRadioModule,
		MatCheckboxModule,
		MatAutocompleteModule,
		MatSlideToggleModule,
		MatTooltipModule,
		MatMenuModule,
		MatChipsModule,
		MatProgressBarModule,
		FileUploadModule,
		NgxMaskDirective,
		NgxMaskPipe,
		TextMaskModule,
		NgChartsModule,
		DragDropModule,
		TranslateModule.forChild({}),
		PdfViewerModule,
		CarouselModule,
		CdkAccordionModule,
		NgxDropzoneModule
	],
	declarations: [
		MethodologyAnimatedComponent,
		LoadingBarComponent,
		LoadingButtonComponent,
		BreadcrumbComponent,
		RelatedSolutionsComponent,
		PaginationComponent,
		ListCitiesComponent,
		HighlightSolutionComponent,
		PaginateComponent,
		NotFoundComponent,
		FooterKnowMoreComponent,
		IndicatorsDataYearEditorComponent,
		IndicatorsSingleDataModalComponent,
		IndicatorsMultipleDataModalComponent,
		UploadImageModalComponent,
		SmallPieChartComponent,
		IndicatorsBenchmarkTableComponent,
		PastYearDirective,
		InputMaskDirective,
		ValidatePercentageDirective,
		OrdinalNumberPipe,
		GenericChipManagerComponent,
		RestrictedInternationalizationComponent,
		FilterReferencePipe,
		FilterArrayPipe,
		SafePipe,
		OdsIndicatorComponent,
		AccordionLandingComponent,
		StayConnectedComponent,
		ButtonComponent,
		CertificationIsoAbntComponent,
		CertificationIsoAbntAreaComponent,
		CertificationIsoAbntAreaFormComponent,
		DialogShowEvidenceComponent,
		DialogAddLinkComponent,
		PlansComponent,
		OurNumbersComponent,
  		DialogAttachEvidenceComponent,
  		CreditCardIconComponent,
		BtnLoadingComponent,
		LoaderComponent,
		CotationContactComponent
	],
	providers: [
		TranslatedLanguage,
		LanguageService,
		TranslateService,
		InternationalizationService,
		CityProfileService,
		SolutionUtilsService,
		RdstationService,
		{provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: {floatLabel: 'auto'}}
	],
  exports: [
			MethodologyAnimatedComponent,
    LoadingBarComponent,
    LoadingButtonComponent,
    HighlightSolutionComponent,
    FooterKnowMoreComponent,
    IndicatorsDataYearEditorComponent,
    TranslateModule,
    UploadImageModalComponent,
    SmallPieChartComponent,
    IndicatorsBenchmarkTableComponent,
    PaginateComponent,
    OrdinalNumberPipe,
    SafePipe,
    PastYearDirective,
    MatChipsModule,
    GenericChipManagerComponent,
    ListCitiesComponent,
    RestrictedInternationalizationComponent,
    MatListModule,
    InputMaskDirective,
    ValidatePercentageDirective,
    FilterReferencePipe,
    FilterArrayPipe,
	NgxMaskDirective,
	NgxMaskPipe,
    AccordionLandingComponent,
    StayConnectedComponent,
    CertificationIsoAbntComponent,
    CertificationIsoAbntAreaComponent,
    CertificationIsoAbntAreaFormComponent,
    PlansComponent,
    OurNumbersComponent,
    ButtonComponent,
    CreditCardIconComponent,
    BtnLoadingComponent,
    LoaderComponent,
    CotationContactComponent,
	OdsIndicatorComponent,
  ],
	schemas: [
		CUSTOM_ELEMENTS_SCHEMA,
	]
})

export class SharedModule {
	constructor(
		public translate: TranslateService,
		public language: LanguageService
	) {
		//Initialize onLoad translation
		this.translate.use(this.language.current().code);
	}
}
